import { useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import { CheckCircle2, ChevronDown, ChevronRight } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import type { Citation, TransparencyMeta } from "@/lib/types";
import {
  isPrassi,
  cleanPrassiName,
  parsePrassiContent,
} from "@/lib/citationHelpers";
import { manualePubblico } from "@/lib/manualiPubblici";
import { riconosciSchema } from "@/lib/schemaImage";
import { fonteSchema } from "@/lib/schemiFonte";
import { apriEsterno } from "@/lib/apriEsterno";

type Brand = "INIM" | "AJAX" | "DAITEM" | "NOTIFIER" | "PRASSI" | "OTHER";

const BRAND_STYLES: Record<Brand, string> = {
  INIM: "bg-blue-100 text-blue-900 dark:bg-blue-950 dark:text-blue-200",
  AJAX: "bg-emerald-100 text-emerald-900 dark:bg-emerald-950 dark:text-emerald-200",
  DAITEM: "bg-red-100 text-red-900 dark:bg-red-950 dark:text-red-200",
  NOTIFIER: "bg-orange-100 text-orange-900 dark:bg-orange-950 dark:text-orange-200",
  // sommesso di proposito: e' una fonte di servizio, non un marchio (04/09/2026)
  PRASSI: "bg-muted text-muted-foreground",
  OTHER: "bg-muted text-muted-foreground",
};

function detectBrand(c: Citation): { brand: Brand; rest: string } {
  const raw = (c.document_name ?? c.title ?? "").trim();
  const ds = (c.dataset_name ?? "").toUpperCase();
  const upper = raw.toUpperCase();
  const candidates: Brand[] = ["INIM", "AJAX", "DAITEM", "NOTIFIER"];
  for (const b of candidates) {
    if (ds.includes(b) || upper.startsWith(b + "__") || upper.startsWith(b + "_") || upper.startsWith(b + "-") || upper.startsWith(b + " ")) {
      // strip leading brand token from raw
      const rest = raw.replace(new RegExp(`^${b}[_\\-\\s]+`, "i"), "");
      return { brand: b, rest };
    }
  }
  return { brand: "OTHER", rest: raw };
}

function cleanDocumentName(name: string): string {
  // remove .pdf
  const s = name.replace(/\.pdf$/i, "");
  const parts = s.split("__").filter(Boolean);
  // drop trailing language code (IT/EN/FR/DE/ES)
  if (parts.length > 1 && /^(IT|EN|FR|DE|ES)$/i.test(parts[parts.length - 1])) {
    parts.pop();
  }
  const cleaned = parts.map((p) => {
    // le sigle modello (con cifre) tengono i trattini; gli underscore
    // sono zavorra dei nomi file e spariscono comunque
    if (/\d/.test(p)) return p.replace(/_/g, " ");
    return p.replace(/[-_]/g, " ");
  });
  return cleaned.join(" — ");
}

function dedup(citations: Citation[]): Citation[] {
  const seen = new Set<string>();
  const out: Citation[] = [];
  for (const c of citations) {
    const k = (c.document_name ?? c.title ?? "").toLowerCase();
    if (!k || seen.has(k)) continue;
    seen.add(k);
    out.push(c);
  }
  return out;
}

function truncate(s: string, max = 200): string {
  if (s.length <= max) return s;
  return s.slice(0, max).trimEnd() + "…";
}

/**
 * Riga di una scheda Datacom: nome cliccabile che si apre in luogo, senza
 * sottotitoli ripetuti — l'avvertenza sta UNA volta sola in fondo al pannello,
 * non dentro ogni scheda (il vecchio riquadro grigio ripetuto era la cosa
 * che a Davide sembrava sporca, 31/08).
 */
function PrassiItem({ c }: { c: Citation }) {
  const [open, setOpen] = useState(false);
  const name = cleanPrassiName(c.document_name);
  const sections = parsePrassiContent(c.content ?? c.snippet ?? "");
  const topic = sections.topic;
  const body = sections.prassi;
  const note = sections.note;
  const fonte = sections.fonte;
  const standard = sections.standard_di_riferimento;
  const haDettaglio = !!(topic || body || note || fonte || standard);
  return (
    <li className="text-sm leading-relaxed sm:text-[13px] sm:leading-snug">
      <div className="flex items-center gap-2.5 px-2.5 py-2">
        <span
          className={`shrink-0 rounded px-1.5 py-0.5 text-[10px] tracking-wide ${BRAND_STYLES.PRASSI}`}
        >
          Datacom
        </span>
        {haDettaglio ? (
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="inline-flex min-w-0 flex-1 items-center gap-1 text-left text-muted-foreground hover:text-foreground hover:underline"
            aria-expanded={open}
          >
            {open ? <ChevronDown size={12} className="shrink-0" /> : <ChevronRight size={12} className="shrink-0" />}
            <span className="truncate">{name}</span>
          </button>
        ) : (
          <span className="min-w-0 flex-1 truncate text-muted-foreground">{name}</span>
        )}
      </div>
      {open && haDettaglio && (
        <div className="mx-2.5 mb-2 space-y-2 rounded-md border border-border/60 bg-background/40 p-3">
          {topic && (
            <h3 className="text-sm font-semibold text-foreground">{topic}</h3>
          )}
          {body && (
            <div className="prose prose-sm dark:prose-invert max-w-none whitespace-pre-wrap text-[13px] leading-relaxed">
              {body}
            </div>
          )}
          {note && (
            <div className="text-xs text-muted-foreground">
              <span className="font-medium text-foreground/80">Nota:</span> {note}
            </div>
          )}
          {(fonte || standard) && (
            <div className="border-t border-border/40 pt-1 text-xs italic text-muted-foreground">
              {fonte && <div>Fonte: {fonte}</div>}
              {standard && <div>Standard: {standard}</div>}
            </div>
          )}
        </div>
      )}
    </li>
  );
}

/**
 * "2 schede tecniche Datacom e 2 manuali NOTIFIER": il piè di pagina dice
 * cosa c'è davvero nell'elenco. Prima diceva "4 manuali Datacom verificati
 * (NOTIFIER)" anche quando le fonti erano quasi tutte schede nostre — e
 * su una domanda normativa il marchio tra parentesi suonava come un errore.
 */
function etichettaTrasparenza(nPrassi: number, nManuali: number, brands: string[]): string | null {
  const pezzi: string[] = [];
  // NIENTE NOME E NIENTE CONTEGGIO (Davide, 04/09/2026): «non vorrei che
  // apparisse come una fonte che fa vedere la citazione, ma che venisse citata
  // si', non cosi' chiaramente». Il numero delle schede raccontava la forma
  // del nostro archivio a chi non deve averci a che fare; il nome del file
  // ancora di piu'. Resta detto che dietro c'e' del sapere di Datacom, che e'
  // l'unica cosa che l'installatore deve sapere.
  if (nPrassi > 0) {
    pezzi.push("conoscenza tecnica Datacom");
  }
  if (nManuali > 0) {
    const marchi = brands.length ? ` ${brands.join(", ")}` : "";
    pezzi.push(nManuali === 1 ? `1 manuale${marchi}` : `${nManuali} manuali${marchi}`);
  }
  if (pezzi.length === 0) return null;
  return `Risposta basata su ${pezzi.join(" e ")}`;
}

export function SourcesList({
  citations,
}: {
  citations?: Citation[];
  /** Tenuto nella firma per i chiamanti esistenti; i conteggi ora si
      derivano sempre dalle citation mostrate, così testo e lista combaciano. */
  transparency?: TransparencyMeta | null;
}) {
  const { isAdmin } = useAuth();
  if (!citations || citations.length === 0) return null;
  const items = dedup(citations);
  if (items.length === 0) return null;

  // Schede Datacom prima, manuali dopo: due famiglie diverse di fonte,
  // mescolarle rendeva l'elenco illeggibile.
  const prassiItems = items.filter((c) => isPrassi(c));
  // Glossari e indici interni (glossario_AJAX.md, kg_INIM.md) sono strumenti
  // del motore, non fonti che l'installatore possa aprire.
  const interno = (c: Citation) => /^(glossario_|kg_)/i.test(c.document_name ?? c.title ?? "");
  const manualItems = items.filter((c) => !isPrassi(c) && !interno(c));
  // LE SCHEDE DATACOM NON SI ELENCANO ALL'INSTALLATORE (Luca Micheli, 02/09/2026:
  // «prassi è una roba che non può cliccare nessuno, la devo proprio togliere»).
  // Contano nel piè di pagina («2 schede tecniche Datacom»), ma la riga
  // senza link confondeva. L'admin le vede, perché deve poterle controllare.
  // I MANUALI PRIMA, la nostra roba dopo (Davide, 04/09/2026: «la prassi la
  // vedo ancora troppo in evidenza»). All'admin serve poterla controllare, ma
  // in cima all'elenco sembrava la fonte principale della risposta mentre e'
  // il complemento: quello che l'installatore deve poter aprire e' il manuale.
  const ordered = isAdmin ? [...manualItems, ...prassiItems] : manualItems;

  const brandSet = new Set<string>();
  for (const c of manualItems) {
    const { brand } = detectBrand(c);
    if (brand !== "OTHER" && brand !== "PRASSI") brandSet.add(brand);
  }
  const label = etichettaTrasparenza(prassiItems.length, manualItems.length, Array.from(brandSet));

  return (
    <div className="mt-4 space-y-1.5">
      <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
        Fonti consultate
      </div>
      <ul className="divide-y divide-border/40 rounded-lg border border-border/50 bg-background/40">
        <TooltipProvider delayDuration={200}>
        {ordered.map((c, i) => {
          if (isPrassi(c)) {
            return <PrassiItem key={`prassi-${i}`} c={c} />;
          }
          const { brand, rest } = detectBrand(c);
          const name = cleanDocumentName(rest || c.title || "Documento");
          const page =
            typeof c.page_number === "number" && Number.isFinite(c.page_number)
              ? `, pag.${c.page_number}`
              : "";
          const hasPage =
            typeof c.page_number === "number" && Number.isFinite(c.page_number);
          // Notifier e Ajax pubblicano da soli la loro documentazione: per quei
          // documenti mandiamo l'installatore alla fonte del costruttore invece
          // di aprire la nostra copia. Non e' solo prudenza legale — e' anche
          // l'unica versione che resta aggiornata quando loro la cambiano.
          const pubblico = manualePubblico(
            c.document_name,
            hasPage ? c.page_number : null,
          );
          // Gli SCHEMA__ non stanno nella tabella dei manuali pubblici: il loro
          // nome non e' un nome di file PDF ma "SCHEMA__<Modello>__p<NNN>__...".
          // Restavano quindi righe morte, non cliccabili — Davide, 04/09/2026:
          // «anche il manuale nelle fonti deve portare al link». Il ponte fra
          // schema e manuale del costruttore ce l'abbiamo gia': fonteSchema.
          const daSchema = riconosciSchema(c.document_name ?? c.title);
          const fonteDelloSchema = daSchema ? fonteSchema(daSchema) : null;
          const pdfHref = fonteDelloSchema
            ? fonteDelloSchema.href
            : pubblico
            ? pubblico.href
            : c.pdf_url
              ? hasPage
                ? `${c.pdf_url}#page=${c.page_number}`
                : c.pdf_url
              : null;
          const snippet = c.snippet ? truncate(c.snippet, 200) : null;

          // su una pagina web del costruttore il numero di pagina non esiste:
          // scriverlo sarebbe un rimando a qualcosa che il lettore non trova
          const mostraPagina = !pubblico || pubblico.forma === "pdf";
          // SENZA USCIRE DALL'APP (Davide, 04/09/2026): «sarebbe il caso di
          // mettere il browser interno, cosi' non li facciamo uscire mai
          // dall'app». Con un <a target="_blank"> il telefono passava a Safari
          // o a Chrome e l'installatore doveva ritrovare Datacom AI da solo;
          // apriEsterno usa la finestra di sistema che si chiude con «Fine».
          // Resta un vero link (href) perche' il tocco lungo, «copia
          // indirizzo» e i lettori di schermo continuino a funzionare.
          const nameNode = pdfHref ? (
            <a
              href={pdfHref}
              onClick={(e) => {
                e.preventDefault();
                void apriEsterno(pdfHref);
              }}
              target="_blank"
              rel="noopener noreferrer"
              className="cursor-pointer text-foreground hover:underline"
            >
              {name}
              {mostraPagina ? page : ""}
            </a>
          ) : (
            <span className="text-foreground">
              {name}
              {mostraPagina ? page : ""}
            </span>
          );

          return (
            <li
              key={`${name}-${i}`}
              className="text-sm leading-relaxed sm:text-[13px] sm:leading-snug"
            >
              {/* Una riga sola, allineata: badge — nome — (dove).

                  IL PULSANTE «SCARICA» NON C'E' PIU' (26/08/2026). Dava una copia
                  del manuale INTERO del costruttore sul telefono di chi chiedeva:
                  non e' consultare una fonte, e' distribuirla. Il nome resta
                  cliccabile e apre il documento alla pagina citata — quello serve
                  a fidarsi della risposta, ed e' un'altra cosa. */}
              <div className="flex items-center gap-2.5 px-2.5 py-2">
                <span
                  className={`shrink-0 rounded px-1.5 py-0.5 font-mono text-[11px] tracking-wide ${BRAND_STYLES[brand]}`}
                >
                  {brand === "OTHER" ? "DOC" : brand}
                </span>
                <span className="min-w-0 flex-1 truncate">
                  {snippet ? (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span>{nameNode}</span>
                      </TooltipTrigger>
                      <TooltipContent
                        side="top"
                        className="w-[400px] bg-popover text-popover-foreground text-xs leading-relaxed"
                      >
                        {snippet}
                      </TooltipContent>
                    </Tooltip>
                  ) : (
                    nameNode
                  )}
                  {(pubblico ?? fonteDelloSchema) && (
                    <span className="ml-1.5 text-xs text-muted-foreground">
                      · sito {(pubblico ?? fonteDelloSchema)!.costruttore}
                    </span>
                  )}
                </span>
              </div>
            </li>
          );
        })}
        </TooltipProvider>
      </ul>
      {label && (
        <div className="mt-2 flex items-center gap-1.5 text-sm italic text-muted-foreground sm:text-xs">
          <CheckCircle2 size={12} className="text-emerald-600 dark:text-emerald-400" />
          <span>{label}</span>
        </div>
      )}
      {prassiItems.length > 0 && (
        <div className="text-[11px] italic text-muted-foreground/70">
          La conoscenza tecnica Datacom è esperienza dei loro tecnici, non
          documentazione ufficiale del costruttore.
        </div>
      )}
    </div>
  );
}
